import {
  getMargenBrutoZonal,
  getZonasProductivasActivas,
} from "@/services/productive-entities.service";
import { getCuentasByBusqueda } from "@/services/accounts.service";
import { Option } from "@/components/select/AsyncAutocompleteSelect";
import { toaster } from "@/components/ui/toaster";
import { Dispatch, RefObject, SetStateAction } from "react";
import type { AxiosError } from "axios";
import {
  getCampanasProductivasActivas,
  getGranosProductivos,
  type GranoProductivo,
} from "@/services/productive-entities.service";
import { CultivoFormItem, ProductPlanificationCreationForm } from "./product-planification.schema";
import { UseFormSetValue } from "react-hook-form";
import { formatCurrencyInputToNumber, parseTonInputToNumber } from "@/utils/query-string";

export const getRhfInputValue = (
  value: unknown,
): string | number | undefined => {
  if (value === null || value === undefined) return undefined;
  if (typeof value === "string" || typeof value === "number") return value;
  const asAny = value as any;
  if (asAny?.target?.value !== undefined) {
    return asAny.target.value;
  }
  return undefined;
};

export const formatDecimalToCommaString = (value: number): string => {
  if (!Number.isFinite(value)) return "";
  const str = String(value);
  return str.replaceAll(".", ",");
};

export interface FetchZonasProps {
  zonaComercial?: string;
  setZonaOptions: Dispatch<SetStateAction<{ label: string; value: string }[]>>;
}
export const fetchZonas = async ({
  zonaComercial,
  setZonaOptions,
}: FetchZonasProps) => {
  const zonas = await getZonasProductivasActivas({ zonaComercial });
  setZonaOptions(
    (zonas ?? []).map((z) => ({
      label: z.nombre,
      value: String(z.id),
    })),
  );
};

export const getAccountOptions = async (
  inputValue: string,
): Promise<Option[]> => {
  const filtered = await getCuentasByBusqueda({ busqueda: inputValue });
  return (
    filtered?.map((p) => ({
      label: `${p.titular} - ${p.nroCuenta} - ${p.tipoCuenta}`,
      value: p.nroCuenta,
      zonaComercial: p.zonaComercial,
    })) ?? []
  );
};

export interface LoadOptionsHandlerProps {
  timeoutRef: RefObject<NodeJS.Timeout | null>;
  inputValue: string;
  setLoadingPersons: Dispatch<SetStateAction<boolean>>;
  getOptions: (inputValue: string) => Promise<Option[]>;
}

export const loadOptionsHandler = ({
  timeoutRef,
  inputValue,
  getOptions,
  setLoadingPersons,
}: LoadOptionsHandlerProps): Promise<Option[]> => {
  return new Promise((resolve) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef?.current);
    }

    timeoutRef.current = setTimeout(() => {
      if (inputValue.trim().length < 3) {
        resolve([]);
        return;
      }

      setLoadingPersons(true);
      const options = getOptions(inputValue);

      setLoadingPersons(false);
      resolve(options);
    }, 1000);
  });
};

export interface FetchStaticOptionsProps {
  setLoadingOptions: Dispatch<SetStateAction<boolean>>;
  setCampanaOptions: Dispatch<
    SetStateAction<{ label: string; value: string }[]>
  >;
  setGranoOptions: Dispatch<SetStateAction<{ label: string; value: string }[]>>;
  setGranosProductivos?: Dispatch<SetStateAction<GranoProductivo[]>>;
}

export const fetchStaticOptions = async ({
  setLoadingOptions,
  setCampanaOptions,
  setGranoOptions,
  setGranosProductivos,
}: FetchStaticOptionsProps) => {
  try {
    setLoadingOptions(true);

    const [campanas, granos] = await Promise.all([
      getCampanasProductivasActivas(),
      getGranosProductivos({ AplicaPPMB: true }),
    ]);

    setCampanaOptions(
      (campanas ?? []).map((c) => ({
        label: c.nombre,
        value: String(c.id),
      })),
    );

    setGranoOptions(
      (granos ?? []).map((g) => ({
        label: g.nombre,
        value: String(g.id),
      })),
    );

    setGranosProductivos?.(granos ?? []);
  } finally {
    setLoadingOptions(false);
  }
};

export interface CalculatePrefilledValuesProps {
  watchedCultivos: CultivoFormItem[];
  setValue: UseFormSetValue<ProductPlanificationCreationForm>;
  idCampana: string;
  idZonaProductiva: string;
  lastMbzKeyByIndexRef: RefObject<Record<number, string>>;
  granosById: Record<number, GranoProductivo>;
}

export const calculatePrefilledValues = async ({
  watchedCultivos,
  setValue,
  idCampana,
  idZonaProductiva,
  lastMbzKeyByIndexRef,
  granosById,
}: CalculatePrefilledValuesProps) => {
  const cultivos = watchedCultivos ?? [];
  const results = await Promise.all(
    cultivos.map(async (c, index) => {
      const idGrano = (c?.idGrano ?? [])?.[0];
      if (!idZonaProductiva || !idGrano) return { missingMbz: false };

      const key = `${idCampana}|${idZonaProductiva}|${idGrano}`;
      const prevKey = lastMbzKeyByIndexRef.current[index];
      if (prevKey === key) return { missingMbz: false };
      lastMbzKeyByIndexRef.current[index] = key;
      const keyChanged = Boolean(prevKey) && prevKey !== key;

      let margen: Awaited<ReturnType<typeof getMargenBrutoZonal>> = null;
      try {
        margen = await getMargenBrutoZonal({
          IdZonaProductiva: Number(idZonaProductiva),
          IdGranoProductivo: Number(idGrano),
          IdCampProd: Number(idCampana),
        });
      } catch (e) {
        const err = e as AxiosError;
        const status = (err as any)?.response?.status;
        if (status === 404) {
          return { missingMbz: true };
        }
        throw e;
      }

      if (!margen) return { missingMbz: true };

      const grano = granosById[Number(idGrano)];
      const precioPizarra = Number(grano?.precioPizarra ?? 0);
      const cotizacionDolar = Number(margen?.cotizacionDolar ?? 0);
      const gastoCosechaPct = Number(margen?.gastoDeCosecha ?? 0);

      const currentTon = parseTonInputToNumber(c?.toneladasRinde);
      const nextTon = Number(margen.rindeEstimadoZonal ?? 0);

      const hasTonText =
        typeof c?.toneladasRinde === "string" && c.toneladasRinde.trim() !== "";

      const toneladasIsNumber = typeof currentTon === "number" && Number.isFinite(currentTon);

      const toneladasAAplicar = toneladasIsNumber ? currentTon : nextTon;
      const toneladasRinde = keyChanged ? nextTon : toneladasAAplicar;

      let costo = 0;
      if (cotizacionDolar > 0) {
        costo =
          toneladasRinde *
          (precioPizarra / cotizacionDolar) *
          (gastoCosechaPct / 100);
      }

      const costoFixed = Number.isFinite(costo) ? Number(costo.toFixed(2)) : 0;

      const hasCostoText =
        typeof c?.costoEstimado === "string" && c.costoEstimado.trim() !== "";

      const currentCosto = formatCurrencyInputToNumber(
        (c?.costoEstimado ?? 0) as any,
      );

      if (keyChanged || !hasTonText || currentTon !== nextTon) {
        setValue(
          `cultivos.${index}.toneladasRinde`,
          formatDecimalToCommaString(nextTon),
        );
      }

      if (keyChanged || !hasCostoText || currentCosto !== costoFixed) {
        setValue(`cultivos.${index}.costoEstimado`, costoFixed as any);
      }

      return { missingMbz: false };
    }),
  );

  const anyMissing = (results ?? []).some((r) => r?.missingMbz);
  if (anyMissing) {
    toaster.create({
      type: "warning",
      title: "No se encontró un margen bruto zonal para los valores proporcionados.",
    });
  }
};
